import { useMemo } from "react";
import { isValid, isYesterday, parseISO } from "date-fns";
import { PortfolioFeedItem, useGetWealthAPIDepotFeed } from "@bn/api/ariva";
import { useSecurityImages } from "@hooks";
import { investmentToSecurityImagePayload } from "@helpers";
import { useSelectedInvestments } from "@features/dashboard/hooks/useSelectedInvestments";
import { mapToArticle } from "./helper";

const parseDate = (value: string): Date => parseISO(value.replace(" ", "T"));

const toSortedArray = (
  bucket: Record<string, PortfolioFeedItem> | undefined
): PortfolioFeedItem[] =>
  Object.values(bucket ?? {})
    .filter((item) => isValid(parseDate(item.date)))
    .sort((a, b) => parseDate(a.date).getTime() - parseDate(b.date).getTime());

export const useArivaTimeline = () => {
  const {
    accountIds,
    investments,
    uniqueIsins,
    isAccountsFetching,
    isInvestmentsLoading,
  } = useSelectedInvestments();

  const securityForLogos = useMemo(
    () => investments.map(investmentToSecurityImagePayload),
    [investments]
  );

  const images = useSecurityImages(securityForLogos);

  const { data: feed, isLoading: isFeedLoading } = useGetWealthAPIDepotFeed(
    { isins: uniqueIsins.join(",") },
    { enabled: uniqueIsins.length > 0 }
  );

  const isLoading =
    isAccountsFetching ||
    accountIds === undefined ||
    (Boolean(accountIds.length) && isInvestmentsLoading) ||
    (uniqueIsins.length > 0 && isFeedLoading);

  const buckets = useMemo(() => {
    const map = (items: PortfolioFeedItem[]) =>
      items.map((item) => mapToArticle(item, images));

    return {
      today: map(toSortedArray(feed?.today)),
      future: map(toSortedArray(feed?.future)),
      past: map(
        toSortedArray(feed?.past).filter((item) =>
          isYesterday(parseDate(item.date))
        )
      ),
    };
  }, [feed, images]);

  const isEmpty =
    !isLoading &&
    buckets.today.length === 0 &&
    buckets.future.length === 0 &&
    buckets.past.length === 0;

  return {
    ...buckets,
    isLoading,
    isEmpty,
  };
};
